
const flatArrIn = (arr: (number | number[])[]) => arr.flatMap(x => x);

const mathApi: Record<string, any> = {
    pow: (base: number, exponent: number): number => Math.pow(base, exponent),
    pi: (): number => Math.PI,
    sum: (...args: any[]): number => {
        args = flatArrIn(args);
        return args.reduce((acc, cur) => acc + cur, 0);
    },
    min: (...args: any[]): number => {
        args = flatArrIn(args);
        return Math.min(...args);
    },
    max: (...args: any[]): number => {
        args = flatArrIn(args);
        return Math.max(...args);
    },
    avg: (...args: any[]): number => {
        args = flatArrIn(args);
        return args.reduce((acc, cur) => acc + cur, 0) / args.length;
    },
    roundTo: (value: number, precision: number): number => {
        const factor = Math.pow(10, precision);
        return Math.round(value * factor) / factor;
    },
    round: (value: number): number => Math.round(value),
    floor: (value: number): number => Math.floor(value),
    ceil: (value: number): number => Math.ceil(value),
    abs: (value: number): number => Math.abs(value),
    sqrt: (value: number): number => Math.sqrt(value),
    // Remember that Math.sin, Math.cos and Math.tan expect radians
    sin: (value: number): number => Math.sin(value),
    cos: (value: number): number => Math.cos(value),
    tan: (value: number): number => Math.tan(value),
};

const toLowerCaseKeys = (obj: Record<string, any>): Record<string, any> =>
    Object.fromEntries(Object.entries(obj).map(([key, value]) => [key.toLowerCase(), value]));

class MflScopeDefaultImpl implements MflScope {

    private functions: Record<string, any>;
    private symbols: Record<string, any>;

    public constructor(
        functions: Record<string, any>,
        symbols: Record<string, any>) {
        this.functions = toLowerCaseKeys(functions);
        this.symbols = toLowerCaseKeys(symbols);
    }

    public getFunction(name: string) {
        return this.functions[name.trim().toLowerCase()];
    }

    public getSymbol(name: string) {
        return this.symbols[name.trim().toLowerCase()];
    }
}

class MflScopeBuilder {

    private functions: Record<string, any> = {};
    private symbols: Record<string, any> = {};

    public addFunction(name: string, func: any): MflScopeBuilder {
        this.functions[name] = func;
        return this;
    }

    public addFunctions(funcs: Record<string, any>): MflScopeBuilder {
        for (const [key, value] of Object.entries(funcs)) {
            this.addFunction(key, value);
        }
        return this;
    }

    public addSymbol(name: string, value: any): MflScopeBuilder {
        this.symbols[name] = value;
        return this;
    }

    public addSymbols(symbols: Record<string, any>): MflScopeBuilder {
        for (const [key, value] of Object.entries(symbols)) {
            this.addSymbol(key, value);
        }
        return this;
    }

    public build() {
        return new MflScopeDefaultImpl(this.functions, this.symbols);
    }
}


export interface MflScope {
    getFunction(name: string): any;
    getSymbol(name: string): any;
}

export const MflScope = Object.freeze({
    createBlank(): MflScopeBuilder {
        return new MflScopeBuilder();
    },
    createDefault(): MflScopeBuilder {
        return this.createBlank().addFunctions(mathApi);
    },
});
